import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface OverrideRule {
  id: string;
  company_id: string;
  source_technician_id: string;
  beneficiary_technician_id: string;
  rate_percentage: number;
  effective_from: string;
  effective_to: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  source_tech?: { id: string; name: string };
  beneficiary_tech?: { id: string; name: string };
  created_by_profile?: { full_name: string };
}

export interface NewOverrideRule {
  company_id: string;
  source_technician_id: string;
  beneficiary_technician_id: string;
  rate_percentage: number;
  effective_from: string;
  effective_to: string | null;
  created_by: string;
}

export interface OverrideFilterOptions {
  isActive?: boolean;
  sourceTechId?: string;
  beneficiaryTechId?: string;
}

export interface JobOverride {
  id: string;
  company_id: string;
  job_id: string;
  source_technician_id: string;
  beneficiary_technician_id: string;
  override_rule_id: string;
  job_net_revenue: number;
  override_rate_percentage: number;
  override_amount: number;
  job_completion_date: string;
  commission_status: 'pending' | 'approved' | 'paid';
  payroll_period_id: string | null;
  created_at: string;
  beneficiary_tech?: { id: string; name: string };
  source_tech?: { id: string; name: string };
}

// Fetch override rules
export const useOverrideRules = (filters?: OverrideFilterOptions) => {
  return useQuery({
    queryKey: ['override-rules', filters],
    queryFn: async () => {
      let query = supabase
        .from('tech_commission_overrides')
        .select(`
          *,
          source_tech:techs!source_technician_id(id, name),
          beneficiary_tech:techs!beneficiary_technician_id(id, name),
          created_by_profile:profiles!created_by(full_name)
        `)
        .order('created_at', { ascending: false });
      
      if (filters?.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      }
      
      if (filters?.sourceTechId) {
        query = query.eq('source_technician_id', filters.sourceTechId);
      }
      
      if (filters?.beneficiaryTechId) {
        query = query.eq('beneficiary_technician_id', filters.beneficiaryTechId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as OverrideRule[];
    }
  });
};

// Check for circular dependencies
export const useCheckCircularDependency = () => {
  return useMutation({
    mutationFn: async (params: {
      company_id: string;
      source_technician_id: string;
      beneficiary_technician_id: string;
      effective_from: string;
      effective_to: string | null;
    }) => {
      const { data, error } = await supabase.rpc(
        'check_override_circular_dependency',
        {
          _company_id: params.company_id,
          _source_tech_id: params.source_technician_id,
          _beneficiary_tech_id: params.beneficiary_technician_id,
          _effective_from: params.effective_from,
          _effective_to: params.effective_to
        }
      );
      
      if (error) throw error;
      return data as boolean;
    }
  });
};

// Create override rule
export const useCreateOverrideRule = () => {
  const queryClient = useQueryClient();
  const checkCircular = useCheckCircularDependency();
  
  return useMutation({
    mutationFn: async (rule: NewOverrideRule) => {
      // Check circular dependency first
      const hasCircular = await checkCircular.mutateAsync({
        company_id: rule.company_id,
        source_technician_id: rule.source_technician_id,
        beneficiary_technician_id: rule.beneficiary_technician_id,
        effective_from: rule.effective_from,
        effective_to: rule.effective_to
      });
      
      if (hasCircular) {
        throw new Error('Circular dependency detected: reverse override rule exists with overlapping dates');
      }
      
      const { data, error } = await supabase
        .from('tech_commission_overrides')
        .insert(rule)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['override-rules'] });
      toast({
        title: 'Override rule created',
        description: 'The commission override rule has been created successfully.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create override rule',
        description: error.message,
        variant: 'destructive'
      });
    }
  });
};

// Update override rule
export const useUpdateOverrideRule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<NewOverrideRule> }) => {
      const { data, error } = await supabase
        .from('tech_commission_overrides')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['override-rules'] });
      toast({
        title: 'Override rule updated',
        description: 'The commission override rule has been updated successfully.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update override rule',
        description: error.message,
        variant: 'destructive'
      });
    }
  });
};

// Delete override rule
export const useDeleteOverrideRule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tech_commission_overrides')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['override-rules'] });
      toast({
        title: 'Override rule deleted',
        description: 'The commission override rule has been deleted successfully.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete override rule',
        description: error.message,
        variant: 'destructive'
      });
    }
  });
};

// Fetch job overrides
export const useJobOverrides = (jobId: string | null) => {
  return useQuery({
    queryKey: ['job-overrides', jobId],
    queryFn: async () => {
      if (!jobId) return [];
      
      const { data, error } = await supabase
        .from('job_commission_overrides')
        .select(`
          *,
          beneficiary_tech:techs!beneficiary_technician_id(id, name),
          source_tech:techs!source_technician_id(id, name)
        `)
        .eq('job_id', jobId)
        .order('beneficiary_technician_id');
      
      if (error) throw error;
      return data as JobOverride[];
    },
    enabled: !!jobId
  });
};
